import { useState } from 'react';
import { email as Api, type MailAccount, type MailLogRow } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Field, Row, SecretInput } from '../components/Field';

const when = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function Email() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));

  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load the email settings — {error.message}</div></div>;
  if (!data) return null;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Email accounts</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            Four purposes, so a newsletter cannot be sent from the account that carries one-time passwords —
            a marketing complaint should never take login mail down with it.
          </p>
        </div>
      </div>

      {!data.can_store_passwords ? (
        <div className="err">
          TVX_SECRET is not set on the server, so passwords cannot be stored safely and these forms will not
          accept one. Set it and redeploy.
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 16 }}>
        {data.accounts.map((a) => (
          <AccountCard key={a.purpose} account={a} canStore={data.can_store_passwords} onSaved={reload} />
        ))}
      </div>

      <Newsletter subscribers={data.subscribers} />

      <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
        <div className="rowbar" style={{ marginBottom: 10 }}>
          <div className="kh">Recent mail</div>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
            {data.stats.sent ?? 0} sent · {data.stats.pending ?? 0} waiting · {data.stats.failed ?? 0} failed
          </span>
        </div>
        {data.log.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>Nothing has been sent yet.</p>
        ) : (
          <div className="tblx">
            <table className="tbl">
              <thead><tr><th>When</th><th>To</th><th>Subject</th><th>Purpose</th><th>Status</th><th /></tr></thead>
              <tbody>
                {data.log.map((row) => <LogRow key={row.id} row={row} onRetried={reload} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function LogRow({ row, onRetried }: { row: MailLogRow; onRetried: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  return (
    <tr>
      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(row.created_at)}</td>
      <td className="muted">{row.to_email}</td>
      <td>{row.subject}
        {row.error ? <div style={{ fontSize: 11.5, color: 'var(--down)' }}>{row.error}</div> : null}</td>
      <td className="muted">{row.purpose}</td>
      <td>
        <span className="tg" style={{
          background: row.status === 'sent' ? 'rgba(62,207,142,.14)'
            : row.status === 'failed' ? 'rgba(240,100,91,.13)' : 'rgba(var(--goldrgb), .14)',
        }}>{row.status}</span>
        {row.attempts > 1 ? <span style={{ fontSize: 11, color: 'var(--tx3)', marginLeft: 6 }}>×{row.attempts}</span> : null}
      </td>
      <td className="right">
        {row.status === 'failed' ? (
          <button className="b bo" style={{ padding: '5px 10px', fontSize: 11.5 }} disabled={busy}
            onClick={async () => { setBusy(true); try { await Api.retry(row.id); await onRetried(); } finally { setBusy(false); } }}>
            Retry
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function AccountCard({ account, canStore, onSaved }: {
  account: MailAccount;
  canStore: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const [f, setF] = useState({ ...account });
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const set = (k: keyof MailAccount, v: unknown) => setF((p) => ({ ...p, [k]: v }) as MailAccount);

  const isDefault = account.purpose === 'default';

  async function save() {
    setError('');
    setStatus('Saving…');
    try {
      /* The password is only sent when it was typed. An absent key means
         "leave it" on the server, which is what lets the host be edited
         without re-entering the password. */
      await Api.save(account.purpose, {
        use_default: f.use_default, host: f.host, port: f.port, encryption: f.encryption,
        username: f.username, from_name: f.from_name, from_email: f.from_email,
        reply_to: f.reply_to, state: f.state,
        ...(password ? { password } : {}),
      });
      setPassword('');
      setStatus('Saved');
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  async function test() {
    if (!testTo.trim()) { setError('Enter an address to send the test to.'); return; }
    setError('');
    setStatus('Sending a test…');
    try {
      await Api.test(account.purpose, testTo.trim());
      setStatus('Test sent — check that inbox.');
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <div className="crd" style={{ padding: '20px 22px' }}>
      <div className="rowbar" style={{ marginBottom: 12 }}>
        <div>
          <div className="kh">{account.label}</div>
          <p className="hint" style={{ margin: '4px 0 0' }}>
            {account.tested_ok
              ? `Tested ${when(account.tested_at)} — it worked.`
              : account.last_error
                ? <span style={{ color: 'var(--down)' }}>Last attempt failed: {account.last_error}</span>
                : 'Never tested.'}
          </p>
        </div>
        <span className="tg" style={{
          background: f.state === 'active' ? 'rgba(62,207,142,.14)' : 'rgba(var(--wash), calc(.06 * var(--washx)))',
        }}>{f.state}</span>
      </div>

      {error ? <div className="err">{error}</div> : null}

      {!isDefault ? (
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={f.use_default} onChange={(e) => set('use_default', e.target.checked)} />
          <span style={{ fontSize: 13.5 }}>Use the default account for this</span>
        </label>
      ) : null}

      {isDefault || !f.use_default ? (
        <>
          <Row>
            <Field label="Host"><input className="inp" value={f.host}
              onChange={(e) => set('host', e.target.value)} /></Field>
            <Field label="Port"><input className="inp" type="number" value={f.port || ''}
              onChange={(e) => set('port', Number(e.target.value))} /></Field>
            <Field label="Encryption">
              <select className="inp" value={f.encryption} onChange={(e) => set('encryption', e.target.value)}>
                <option value="starttls">STARTTLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select></Field>
          </Row>
          <Row>
            <Field label="Username"><input className="inp" value={f.username}
              onChange={(e) => set('username', e.target.value)} /></Field>
            <Field
              label="Password"
              hint={account.has_password
                ? (account.password_readable
                  ? 'Stored. Leave blank to keep it.'
                  : 'Stored but no longer readable — the key changed. Enter it again.')
                : 'Not set.'}
            >
              <SecretInput disabled={!canStore} value={password}
                placeholder={account.has_password ? 'Unchanged' : ''}
                onChange={setPassword} /></Field>
            <Field label="State">
              <select className="inp" value={f.state} onChange={(e) => set('state', e.target.value)}>
                <option value="active">active</option>
                <option value="off">off</option>
              </select></Field>
          </Row>
          <Row>
            <Field label="From name"><input className="inp" value={f.from_name}
              onChange={(e) => set('from_name', e.target.value)} /></Field>
            <Field label="From email"><input className="inp" value={f.from_email}
              onChange={(e) => set('from_email', e.target.value)} /></Field>
            <Field label="Reply-to"><input className="inp" value={f.reply_to}
              onChange={(e) => set('reply_to', e.target.value)} /></Field>
          </Row>
        </>
      ) : null}

      <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
        <input className="inp" style={{ maxWidth: 260 }} placeholder="Send a test to…"
          value={testTo} onChange={(e) => setTestTo(e.target.value)} />
        <button className="b bo" type="button" onClick={() => void test()}>Send test</button>
        <span className="mdl-spacer" style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
        <button className="b bg" type="button" onClick={() => void save()}>Save</button>
      </div>
    </div>
  );
}

/** A real send, to real people, so it says how many before it goes. */
function Newsletter({ subscribers }: { subscribers: number }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  async function send() {
    if (!subject.trim() || !body.trim()) { setError('A subject and something to say are both needed.'); return; }
    if (!confirm(`Send “${subject}” to ${subscribers} subscriber${subscribers === 1 ? '' : 's'}? This cannot be unsent.`)) return;
    setError('');
    setStatus('Queueing…');
    try {
      const r = await Api.newsletter(subject.trim(), body.trim());
      setStatus(`Queued to ${r.queued} ${r.queued === 1 ? 'person' : 'people'}.`);
      setSubject(''); setBody('');
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
      <div className="kh" style={{ marginBottom: 6 }}>Newsletter</div>
      <p className="hint" style={{ marginBottom: 14 }}>
        Goes to <strong>{subscribers}</strong> {subscribers === 1 ? 'subscriber' : 'subscribers'} through the
        newsletter account. There is no unsend.
      </p>
      {error ? <div className="err">{error}</div> : null}
      <Field label="Subject">
        <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <Field label="Message" hint="A blank line starts a new paragraph.">
        <textarea className="inp ta" rows={6} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="b bg" type="button" disabled={!subscribers} onClick={() => void send()}>
          Send to {subscribers}
        </button>
        <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
      </div>
    </div>
  );
}
